import React, { useEffect, useMemo, useState } from "react";
import Navbar from "../components/profile-components/Navbar";
import Topbar from "../components/profile-components/Topbar";
import API_BASE_URL from "../config";
import "./SalesPages.css";
import "./Support.css";

function Support() {
  const [formData, setFormData] = useState({
    category: "Issue / Bug",
    priority: "Normal",
    subject: "",
    message: "",
  });

  const [screenshots, setScreenshots] = useState([]);
  const [tickets, setTickets] = useState([]);
  const [selectedTicketId, setSelectedTicketId] = useState(null);
  const [selectedTicket, setSelectedTicket] = useState(null);
  const [messages, setMessages] = useState([]);
  const [attachments, setAttachments] = useState([]);

  const [replyMessage, setReplyMessage] = useState("");
  const [replyScreenshots, setReplyScreenshots] = useState([]);

  const [loadingTickets, setLoadingTickets] = useState(true);
  const [loadingTicketDetail, setLoadingTicketDetail] = useState(false);
  const [submitting, setSubmitting] = useState(false);
  const [submittingReply, setSubmittingReply] = useState(false);
  const [openingAttachmentId, setOpeningAttachmentId] = useState(null);

  const [success, setSuccess] = useState("");
  const [error, setError] = useState("");

  const allowedScreenshotTypes = new Set([
    "image/png",
    "image/jpeg",
    "image/jpg",
    "image/webp",
  ]);

  const isSelectedTicketClosed = selectedTicket?.status === "Closed";

  const attachmentsByMessageId = useMemo(() => {
    return attachments.reduce((acc, attachment) => {
      const key = attachment.support_ticket_message_id || "ticket";

      if (!acc[key]) {
        acc[key] = [];
      }

      acc[key].push(attachment);
      return acc;
    }, {});
  }, [attachments]);

  const formatDate = (value) => {
    if (!value) return "—";

    const date = new Date(value);

    if (Number.isNaN(date.getTime())) return "—";

    return date.toLocaleString();
  };

  const formatFileSize = (value) => {
    const size = Number(value || 0);

    if (!size) return "—";

    if (size < 1024 * 1024) {
      return `${Math.ceil(size / 1024)} KB`;
    }

    return `${(size / (1024 * 1024)).toFixed(1)} MB`;
  };

  const loadTickets = async () => {
    try {
      setLoadingTickets(true);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/support-tickets`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load support tickets");
      }

      const nextTickets = Array.isArray(data) ? data : [];
      setTickets(nextTickets);

      if (!selectedTicketId && nextTickets.length > 0) {
        await loadTicketDetail(nextTickets[0].id);
      }
    } catch (err) {
      setError(err.message || "Failed to load support tickets");
      setTickets([]);
    } finally {
      setLoadingTickets(false);
    }
  };

  const loadTicketDetail = async (ticketId) => {
    if (!ticketId) return;

    try {
      setLoadingTicketDetail(true);
      setError("");
      setSuccess("");

      const token = localStorage.getItem("token");

      const res = await fetch(`${API_BASE_URL}/api/support-tickets/${ticketId}`, {
        headers: {
          Authorization: `Bearer ${token}`,
        },
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to load support ticket");
      }

      setSelectedTicket(data.ticket || null);
      setMessages(Array.isArray(data.messages) ? data.messages : []);
      setAttachments(Array.isArray(data.attachments) ? data.attachments : []);
      setSelectedTicketId(ticketId);
      setReplyMessage("");
      setReplyScreenshots([]);

      const screenshotInput = document.getElementById("support-reply-screenshots");
      if (screenshotInput) {
        screenshotInput.value = "";
      }
    } catch (err) {
      setError(err.message || "Failed to load support ticket");
      setSelectedTicket(null);
      setMessages([]);
      setAttachments([]);
    } finally {
      setLoadingTicketDetail(false);
    }
  };

  useEffect(() => {
    loadTickets();
  }, []);

  const validateScreenshots = (files, inputElement) => {
    if (files.length > 5) {
      setError("You can upload up to 5 screenshots.");
      inputElement.value = "";
      return null;
    }

    const invalidFile = files.find(
      (file) => !allowedScreenshotTypes.has(file.type)
    );

    if (invalidFile) {
      setError("Only PNG, JPG, JPEG, and WEBP screenshots are allowed.");
      inputElement.value = "";
      return null;
    }

    const oversizedFile = files.find(
      (file) => file.size > 10 * 1024 * 1024
    );

    if (oversizedFile) {
      setError("Each screenshot must be 10 MB or smaller.");
      inputElement.value = "";
      return null;
    }

    return files;
  };

  const handleChange = (event) => {
    const { name, value } = event.target;

    setFormData((prev) => ({
      ...prev,
      [name]: value,
    }));
  };

  const handleScreenshotChange = (event) => {
    setError("");
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = validateScreenshots(selectedFiles, event.target);
    setScreenshots(validFiles || []);
  };

  const handleReplyScreenshotChange = (event) => {
    setError("");
    const selectedFiles = Array.from(event.target.files || []);
    const validFiles = validateScreenshots(selectedFiles, event.target);
    setReplyScreenshots(validFiles || []);
  };

  const handleOpenAttachment = async (attachmentId) => {
    try {
      setOpeningAttachmentId(attachmentId);
      setError("");

      const token = localStorage.getItem("token");

      const res = await fetch(
        `${API_BASE_URL}/api/support-tickets/attachments/${attachmentId}/download`,
        {
          headers: {
            Authorization: `Bearer ${token}`,
          },
        }
      );

      if (!res.ok) {
        let message = "Failed to open screenshot";

        try {
          const data = await res.json();
          message = data.error || data.message || message;
        } catch {
          // Response may not be JSON.
        }

        throw new Error(message);
      }

      const blob = await res.blob();
      const blobUrl = window.URL.createObjectURL(blob);

      window.open(blobUrl, "_blank", "noopener,noreferrer");

      setTimeout(() => {
        window.URL.revokeObjectURL(blobUrl);
      }, 60000);
    } catch (err) {
      setError(err.message || "Failed to open screenshot");
    } finally {
      setOpeningAttachmentId(null);
    }
  };

  const renderAttachments = (messageId) => {
    const messageAttachments = attachmentsByMessageId[messageId] || [];

    if (messageAttachments.length === 0) {
      return null;
    }

    return (
      <div className="support-attachment-list">
        {messageAttachments.map((attachment) => (
          <button
            key={attachment.id}
            type="button"
            className="support-attachment-item"
            onClick={() => handleOpenAttachment(attachment.id)}
            disabled={openingAttachmentId === attachment.id}
          >
            <span>{attachment.original_file_name || "Screenshot"}</span>
            <strong>
              {openingAttachmentId === attachment.id
                ? "Opening..."
                : formatFileSize(attachment.file_size_bytes)}
            </strong>
          </button>
        ))}
      </div>
    );
  };

  const handleSubmit = async (event) => {
    event.preventDefault();

    setSuccess("");
    setError("");

    if (!formData.subject.trim()) {
      setError("Please enter a subject.");
      return;
    }

    if (!formData.message.trim()) {
      setError("Please describe what you need help with.");
      return;
    }

    try {
      setSubmitting(true);

      const token = localStorage.getItem("token");
      const payload = new FormData();

      payload.append("category", formData.category);
      payload.append("priority", formData.priority);
      payload.append("subject", formData.subject);
      payload.append("message", formData.message);

      screenshots.forEach((file) => {
        payload.append("screenshots", file);
      });

      const res = await fetch(`${API_BASE_URL}/api/support-tickets`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to submit support request");
      }

      setSuccess("Support request submitted.");
      setFormData({
        category: "Issue / Bug",
        priority: "Normal",
        subject: "",
        message: "",
      });
      setScreenshots([]);

      const screenshotInput = document.getElementById("support-screenshots");
      if (screenshotInput) {
        screenshotInput.value = "";
      }

      await loadTickets();

      if (data.ticket?.id) {
        await loadTicketDetail(data.ticket.id);
      }
    } catch (err) {
      setError(err.message || "Failed to submit support request");
    } finally {
      setSubmitting(false);
    }
  };

  const handleReplySubmit = async (event) => {
    event.preventDefault();

    setSuccess("");
    setError("");

    if (!selectedTicketId) {
      setError("Select a support ticket first.");
      return;
    }

    if (isSelectedTicketClosed) {
      setError("This ticket is closed. Please submit a new support request if you need more help.");
      return;
    }

    if (!replyMessage.trim()) {
      setError("Please enter a reply.");
      return;
    }

    try {
      setSubmittingReply(true);

      const token = localStorage.getItem("token");
      const payload = new FormData();

      payload.append("message", replyMessage);

      replyScreenshots.forEach((file) => {
        payload.append("screenshots", file);
      });

      const res = await fetch(`${API_BASE_URL}/api/support-tickets/${selectedTicketId}/messages`, {
        method: "POST",
        headers: {
          Authorization: `Bearer ${token}`,
        },
        body: payload,
      });

      const data = await res.json();

      if (!res.ok) {
        throw new Error(data.error || data.message || "Failed to send reply");
      }

      setSuccess("Reply sent.");
      setReplyMessage("");
      setReplyScreenshots([]);

      const screenshotInput = document.getElementById("support-reply-screenshots");
      if (screenshotInput) {
        screenshotInput.value = "";
      }

      await loadTickets();
      await loadTicketDetail(selectedTicketId);
    } catch (err) {
      setError(err.message || "Failed to send reply");
    } finally {
      setSubmittingReply(false);
    }
  };

  return (
    <>
      <Navbar />
      <Topbar />

      <div className="main-content sales-page">
        <div className="sales-hero support-compact-hero">
          <div>
            <p className="sales-eyebrow">Agent Book CRM</p>
            <h1 className="sales-title">Help / Support</h1>
            <p className="sales-subtitle">
              Search the Knowledge Base for guides and troubleshooting, or submit a support request if you still need help.
            </p>
          </div>

          <a
            href="https://help.agentbookcrm.com/help"
            target="_blank"
            rel="noreferrer"
            className="sales-primary-btn"
          >
            Visit Knowledge Base
          </a>
        </div>

        {success && (
          <div className="support-ticket-message success">
            {success}
          </div>
        )}

        {error && (
          <div className="support-ticket-message error">
            {error}
          </div>
        )}

        <div className="sales-table-card support-request-card">
          <details>
            <summary>
              <span>New Support Request</span>
              <small>Report an issue, ask a question, or share a recommendation.</small>
            </summary>

            <form onSubmit={handleSubmit} className="support-ticket-form">
              <div className="support-ticket-form-grid">
                <label className="support-ticket-field">
                  Category
                  <select
                    name="category"
                    value={formData.category}
                    onChange={handleChange}
                  >
                    <option value="Issue / Bug">Issue / Bug</option>
                    <option value="Question">Question</option>
                    <option value="Recommendation">Recommendation</option>
                    <option value="Feature Request">Feature Request</option>
                  </select>
                </label>

                <label className="support-ticket-field">
                  Priority
                  <select
                    name="priority"
                    value={formData.priority}
                    onChange={handleChange}
                  >
                    <option value="Low">Low</option>
                    <option value="Normal">Normal</option>
                    <option value="High">High</option>
                    <option value="Urgent">Urgent</option>
                  </select>
                </label>
              </div>

              <label className="support-ticket-field">
                Subject
                <input
                  type="text"
                  name="subject"
                  value={formData.subject}
                  onChange={handleChange}
                  placeholder="Briefly summarize your request"
                />
              </label>

              <label className="support-ticket-field">
                Message
                <textarea
                  name="message"
                  value={formData.message}
                  onChange={handleChange}
                  rows="5"
                  placeholder="Describe the issue, question, or recommendation..."
                />
              </label>

              <label className="support-ticket-field">
                Screenshots
                <input
                  id="support-screenshots"
                  type="file"
                  accept="image/png,image/jpeg,image/jpg,image/webp"
                  multiple
                  onChange={handleScreenshotChange}
                />
                <span className="support-ticket-help-text">
                  Optional. Upload up to 5 screenshots. PNG, JPG, JPEG, or WEBP only.
                </span>
              </label>

              {screenshots.length > 0 && (
                <div className="support-selected-files">
                  {screenshots.map((file) => (
                    <div key={`${file.name}-${file.size}`}>
                      <span>{file.name}</span>
                      <strong>{formatFileSize(file.size)}</strong>
                    </div>
                  ))}
                </div>
              )}

              <div className="support-ticket-actions">
                <button
                  type="submit"
                  className="sales-primary-btn"
                  disabled={submitting}
                >
                  {submitting ? "Submitting..." : "Submit Request"}
                </button>
              </div>
            </form>
          </details>
        </div>

        <div className="support-workspace">
          <div className="sales-table-card support-ticket-list-card">
            <div className="sales-table-header">
              <div>
                <h2>My Tickets</h2>
                <p>Select a ticket to view the conversation.</p>
              </div>
            </div>

            {loadingTickets ? (
              <div className="support-empty-message">Loading tickets...</div>
            ) : tickets.length === 0 ? (
              <div className="support-empty-message">
                No support tickets yet.
              </div>
            ) : (
              <div className="sales-table-wrap">
                <table className="sales-table support-ticket-table">
                  <thead>
                    <tr>
                      <th>Ticket</th>
                      <th>Status</th>
                      <th>Updated</th>
                    </tr>
                  </thead>
                  <tbody>
                    {tickets.map((ticket) => (
                      <tr
                        key={ticket.id}
                        className={`sales-clickable-row ${
                          selectedTicketId === ticket.id ? "support-selected-row" : ""
                        }`}
                        onClick={() => loadTicketDetail(ticket.id)}
                      >
                        <td>
                          <strong>#{ticket.id}</strong>
                          <br />
                          <span>{ticket.subject}</span>
                        </td>
                        <td>{ticket.status || "Open"}</td>
                        <td>{formatDate(ticket.updated_at || ticket.created_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          <div className="sales-table-card support-detail-card">
            <div className="sales-table-header">
              <div>
                <h2>
                  {selectedTicket ? `Ticket #${selectedTicket.id}` : "Ticket Detail"}
                </h2>
                <p>
                  {selectedTicket
                    ? selectedTicket.subject
                    : "Select a ticket to view messages and screenshots."}
                </p>
              </div>
            </div>

            {loadingTicketDetail ? (
              <div className="support-empty-message">Loading ticket...</div>
            ) : !selectedTicket ? (
              <div className="support-empty-message">
                Select a support ticket from the table.
              </div>
            ) : (
              <div className="support-detail-body">
                <div className="support-ticket-meta-line">
                  <strong>Status:</strong> {selectedTicket.status || "Open"} ·{" "}
                  <strong>Category:</strong> {selectedTicket.category || "—"} ·{" "}
                  <strong>Priority:</strong> {selectedTicket.priority || "—"} ·{" "}
                  <strong>Created:</strong> {formatDate(selectedTicket.created_at)}
                </div>

                <div className="support-message-list">
                  {messages.map((item) => (
                    <div key={item.id} className="support-thread-message">
                      <div className="support-thread-meta">
                        <strong>{item.sender_name || item.sender_email || "Support"}</strong>
                        <span>{formatDate(item.created_at)}</span>
                      </div>
                      <p>{item.message}</p>
                      {renderAttachments(item.id)}
                    </div>
                  ))}
                </div>

                {isSelectedTicketClosed ? (
                  <div className="support-closed-message">
                    This ticket is closed. Please submit a new support request if you need more help.
                  </div>
                ) : (
                  <form onSubmit={handleReplySubmit} className="support-reply-box">
                    <label className="support-ticket-field">
                      Reply
                      <textarea
                        value={replyMessage}
                        onChange={(event) => setReplyMessage(event.target.value)}
                        rows="4"
                        placeholder="Reply to support..."
                      />
                    </label>

                    <div className="support-reply-actions">
                      <label className="support-file-picker">
                        Add screenshots
                        <input
                          id="support-reply-screenshots"
                          type="file"
                          accept="image/png,image/jpeg,image/jpg,image/webp"
                          multiple
                          onChange={handleReplyScreenshotChange}
                        />
                      </label>

                      <button
                        type="submit"
                        className="sales-primary-btn"
                        disabled={submittingReply}
                      >
                        {submittingReply ? "Sending..." : "Send Reply"}
                      </button>
                    </div>

                    {replyScreenshots.length > 0 && (
                      <div className="support-selected-files compact">
                        {replyScreenshots.map((file) => (
                          <div key={`${file.name}-${file.size}`}>
                            <span>{file.name}</span>
                            <strong>{formatFileSize(file.size)}</strong>
                          </div>
                        ))}
                      </div>
                    )}
                  </form>
                )}
              </div>
            )}
          </div>
        </div>
      </div>
    </>
  );
}

export default Support;